import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { getBillingSubscriptions } from '../../../actions/billingActions';
import { colorStack } from '../../../styleHelpers/colors';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import { headersShadow } from '../../../styleHelpers/mixins/shadow';
import { IBillingSubscription } from '../../../entities/Billing/Billing';
import { Box } from '../../Common/Box/Box';
import { Row } from '../../Common/Row/Row';
import { LabelWithEllipsis } from '../../Common/Label/Label';
import { formatDurationDate } from '../../../tools/date';
import IntlMessage from '../../Common/IntlMessage';

type GetBillingSubscriptions = ReturnType<typeof getBillingSubscriptions>;

const Container = styled.div`
    max-width:1280px;
    margin: 0 auto;
`;

const Value = styled.div`
    color: ${colorStack.content};
    ${fontSizeAndHeight[16]};
    overflow-wrap: break-word;
    white-space: pre-line;
`;

const HeaderWrapper = styled.div`
    border-radius: 4px;
    background: ${colorStack.whiteGrey};
    padding: 1rem 3rem;
    ${headersShadow()};
    position: relative;
    margin-bottom: 10px;
    span {
        ${fontSizeAndHeight[16]};
        color: ${colorStack.darkBlue};
        border-bottom: 4px solid ${colorStack.middleBlue};
    };
`;

const Tip = styled.div`
    text-align: left;
    color: ${colorStack.darkGrey};
    margin-top: 5px;
    font-weight:400;
`;

export const Billing: FC<React.PropsWithChildren<unknown>> = () => {
    const dispatch = useDispatch();
    const [subscriptions, setSubscriptions] = useState<IBillingSubscription[]>(undefined);

    useEffect(() => {
        dispatch<GetBillingSubscriptions>(getBillingSubscriptions()).then((res) => setSubscriptions(res));
    }, []);

    return (
        <Container>
            <HeaderWrapper>
                <span><IntlMessage id="billing.title" /></span>
                <Tip><IntlMessage id="billing.tip1" /></Tip>
                <Tip><IntlMessage id="billing.tip2" /></Tip>
            </HeaderWrapper>
            {subscriptions?.map(elem =>
                <Box title={elem.name} key={elem.name}>
                    <Row numberOfElements={3}>
                        <div>
                            <LabelWithEllipsis>
                                <IntlMessage id="billing.status" />
                            </LabelWithEllipsis>
                            <Value>{elem.saasSubscriptionStatus}</Value>
                        </div>
                        <div>
                            <LabelWithEllipsis>
                                <IntlMessage id="billing.startDate" />
                            </LabelWithEllipsis>
                            <Value>{moment(elem.term.startDate).format('DD MMM YYYY')}</Value>
                        </div>
                        <div>
                            <LabelWithEllipsis>
                                <IntlMessage id="billing.endDate" />
                            </LabelWithEllipsis>
                            <Value>{moment(elem.term.endDate).format('DD MMM YYYY')}</Value>
                        </div>
                    </Row>
                    <Row numberOfElements={3}>
                        <div>
                            <LabelWithEllipsis>
                                <IntlMessage id="billing.period" />
                            </LabelWithEllipsis>
                            <Value>{formatDurationDate(elem.term.termUnit)}</Value>
                        </div>
                        <div>
                            <LabelWithEllipsis>
                                <IntlMessage id="billing.renew" />
                            </LabelWithEllipsis>
                            <Value>{elem.autoRenew ? <IntlMessage id="global.yes" /> : <IntlMessage id="global.no" />}</Value>
                        </div>
                        <div></div>
                    </Row>
                </Box>
            )}
        </Container>
    );
};

export default Billing;